import {useCallback} from 'react';

import NiceModal, {useModal} from '@ebay/nice-modal-react';
import {Observer} from 'mobx-react';

import type {TextInputField} from '@yourcoach/shared/utils/validation/createTextInputField';

import {useI18n} from '@yourcoach/i18n';
import {fileStore} from '@yourcoach/shared/api/media/file';
import {alert} from '@yourcoach/shared/components/alert';
import {Form} from '@yourcoach/shared/components/Form';
import {CRUProgramScheduleTaskStore} from '@yourcoach/shared/modules/program/cru/schedule';
import {LoadingSpinner} from '@yourcoach/shared/uikit/LoadingSpinner';
import {Toggle} from '@yourcoach/shared/uikit/Toggle';
import {
  createFile,
  getFileCategoriesByMimetype,
} from '@yourcoach/shared/utils/entity/file';
import {payloadActionCreator} from '@yourcoach/shared/utils/events';
import {useMobxStore} from '@yourcoach/shared/utils/store';
import {FlagManager} from '@yourcoach/shared/utils/store/FlagManager';
import {TestID} from '@yourcoach/shared/utils/test-utils/data-test-id';

import AddFileModal from '@src/modules/library/AddFileModal';

import type {Props} from './types';

import {
  Container,
  List,
  ModalTask,
  useCRUScheduleEvent,
} from '../useCRUScheduleEvent';
import * as S from './styles';

export const CRUProgramScheduleTaskModal = NiceModal.create<Props>(
  ({program, task}) => {
    const modal = useModal();
    const {t} = useI18n();
    const overlayIsVisible = useMobxStore(() => new FlagManager());
    const addFileModalFlag = useMobxStore(() => new FlagManager());

    const store = useMobxStore(
      () => new CRUProgramScheduleTaskStore({program, task}),
    );

    const onRequestClose = useCallback(() => {
      modal.resolve();
      modal.hide();
    }, [modal]);

    const onSave = useCallback(async () => {
      try {
        overlayIsVisible.setTrue();

        const taskToSave = await store.getTask();

        if (taskToSave) {
          modal.resolve(taskToSave);
          modal.hide();
        } else {
          Object.keys(store.fields)
            .map(key => store.fields[key])
            .some((field: TextInputField) => {
              if (!field.isValid) {
                field.ref.current?.focus();

                return true;
              }

              return false;
            });
        }
      } catch (e) {
        alert.show({
          message: e.message,
          title: t('shared:message.error'),
        });
      } finally {
        overlayIsVisible.setFalse();
      }
    }, [modal, overlayIsVisible, store, t]);

    const {
      daySection,
      descriptionSection,
      footer,
      repeatSection,
      timeSection,
      titleSection,
    } = useCRUScheduleEvent(store, onSave, 'task');

    const onAddAttachmentButtonClick = useCallback(() => {
      addFileModalFlag.setTrue();
    }, [addFileModalFlag]);

    const onAddAttachment = useCallback(
      (files: File[]) => {
        addFileModalFlag.setFalse();

        files.forEach(file => {
          const name = file.name || 'Attachment';
          const newAttachment = createFile({
            categories: getFileCategoriesByMimetype(file.type),
            id: Date.now().toString(),
            mimetype: file.type,
            name,
            size: file.size,
            // TODO: Where to release resources? (URL.revokeObjectURL)
            url: URL.createObjectURL(file),
          });

          fileStore.upload(file, {
            name,
            uploadingKey: newAttachment._id,
          });

          store.onAddAttachments(payloadActionCreator([newAttachment]));
        });
      },
      [store, addFileModalFlag],
    );

    const removeAttachment = useCallback(
      attachment => {
        store.onRemoveAttachment(payloadActionCreator(attachment));
      },
      [store],
    );

    return (
      <ModalTask
        bodyClassName="body"
        isOpen={modal.visible}
        onAfterClose={modal.remove}
        onRequestClose={onRequestClose}
        title={store.title}
      >
        <Container>
          <List customScrollbar>
            <Form.Row>
              {titleSection}
              {repeatSection}
            </Form.Row>
            <Form.Row>
              {daySection}
              <Observer>
                {() =>
                  store.isRecurring ? (
                    <Form.Section>
                      <Form.LabelContainer>
                        <Form.Label>
                          {t('shared:CRUProgramScheduleTask.stop_day_label')}
                        </Form.Label>
                        <S.StopDateToggleContainer>
                          <Toggle
                            isActive={!!store.stopDay}
                            onClick={store.toggleStopDay}
                          />
                        </S.StopDateToggleContainer>
                      </Form.LabelContainer>
                      <Form.TextInput
                        error={store.fields.stopDay.error || ''}
                        onChange={store.onStopDayChange}
                        placeholder={t(
                          'shared:CRUProgramScheduleTask.default_stop_day_label',
                        )}
                        ref={store.fields.stopDay.ref}
                        testID={TestID.schedule_event.input.stop_day}
                        value={store.fields.stopDay.value}
                      />
                    </Form.Section>
                  ) : null
                }
              </Observer>
              <Observer>
                {() => (!store.isRecurring ? timeSection : null)}
              </Observer>
            </Form.Row>
            <Observer>
              {() =>
                store.isRecurring ? <Form.Row>{timeSection}</Form.Row> : null
              }
            </Observer>
            <Form.Section>
              <Form.Label>
                {t('shared:CRUProgramScheduleTask.attachments_label')}
              </Form.Label>
              <Observer>
                {() =>
                  store.attachments.length ? (
                    <S.AttachmentsList>
                      {store.attachments.map(attachment => (
                        <S.AttachmentsListItem
                          file={attachment}
                          key={attachment._id}
                          onDeleteButtonClick={removeAttachment}
                        />
                      ))}
                    </S.AttachmentsList>
                  ) : null
                }
              </Observer>
              <S.AddAttachmentsButton onClick={onAddAttachmentButtonClick}>
                <S.AddAttachmentsButtonIcon />
                {t('shared:CRUProgramScheduleTask.add_attachment_button')}
              </S.AddAttachmentsButton>
            </Form.Section>
            {descriptionSection}
          </List>
        </Container>
        {footer}
        <Observer>
          {() => (
            <AddFileModal
              isOpen={addFileModalFlag.isTrue}
              mode={'select'}
              multiple
              onRequestClose={addFileModalFlag.setFalse}
              onSelect={onAddAttachment}
            />
          )}
        </Observer>
        <Observer>
          {() =>
            overlayIsVisible.isTrue ? (
              <div className="overlay">
                <LoadingSpinner size={100} />
              </div>
            ) : null
          }
        </Observer>
      </ModalTask>
    );
  },
);
